import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ContentAssetCreateInput } from "@colab/shared";

export type CreateAssetResponse = { id: string };

// POST /api/admin/content/assets
//
// Handles three of the four legacy shapes (folder / external link / embed).
// The file shape lives in use-upload-content-asset.ts because it needs to
// orchestrate the init → uploadToSignedUrl → POST dance; this hook simply
// expects `file_url` to be empty or already set by the upload flow.
//
// Replaces revalidatePath("/admin/content") from
// /app/admin/content/_actions.ts::createContentAsset.
export function useCreateAsset() {
  const queryClient = useQueryClient();
  return useMutation<CreateAssetResponse, ApiClientError, ContentAssetCreateInput>({
    mutationFn: (input) =>
      apiFetch<CreateAssetResponse>("/admin/content/assets", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, input) => {
      // Folder creates also mutate the Move-dialog source — sweep
      // `folders()` alongside the list invalidate when a folder lands.
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      if (input.is_folder) {
        queryClient.invalidateQueries({ queryKey: queryKeys.content.folders() });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create asset");
    },
  });
}
